import { useState, type FormEvent } from 'react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';
import { api } from '@/lib/api';

export default function NewsletterSectionV2() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'ok' | 'error'>('idle');

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || status === 'submitting') return;
    setStatus('submitting');
    try {
      await api.post('/newsletter', { email, source: 'blog' });
      setStatus('ok');
      setEmail('');
    } catch {
      // Don't claim success if the signup didn't persist (the whole point of M9/M1:
      // never show "✓ Inscrit" for a lost email).
      setStatus('error');
    }
  };

  return (
    <section className="bg-lime-500 py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="grid gap-12 md:grid-cols-[1fr_1fr] md:items-center md:gap-16">
          <div>
            <SectionLabel className="text-charcoal-800">La lettre du lundi</SectionLabel>
            <h2 className="mt-6 font-marketing-display text-[clamp(34px,5vw,64px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-950">
              Un article.
              <br />
              <span className="bg-charcoal-800 px-2 text-lime-400">Un lundi sur deux.</span>
            </h2>
            <p className="mt-5 max-w-md font-marketing-body text-[17px] leading-[1.55] text-charcoal-800/90">
              Les articles qu'on écrit, résumés courts, directement dans ta boîte mail. Pas de spam,
              pas de pub.
            </p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <label
              htmlFor="newsletter-email"
              className="font-marketing-mono text-[11px] font-bold uppercase tracking-[0.14em] text-charcoal-800"
            >
              Ton email
            </label>
            <div className="flex flex-col gap-2 md:flex-row">
              <input
                id="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="toi@taboutique.com"
                className="flex-1 border-2 border-charcoal-800 bg-white px-4 py-3 font-marketing-body text-[15px] text-charcoal-800 shadow-neobrutal-3 focus:shadow-neobrutal-4 focus:outline-none"
              />
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex items-center justify-center gap-2 border-2 border-charcoal-800 bg-charcoal-800 px-6 py-3 font-marketing-display text-[14.5px] font-bold text-lime-400 shadow-neobrutal-3 transition-transform hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:hover:shadow-neobrutal-3"
              >
                {status === 'submitting' ? 'Envoi…' : "S'abonner"}
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M5 12h14" />
                  <path d="M13 6l6 6-6 6" />
                </svg>
              </button>
            </div>
            <div
              className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-700"
              aria-live="polite"
            >
              {status === 'ok' && '✓ Inscrit · Tu reçois la prochaine lettre lundi prochain'}
              {status === 'error' && '✕ Échec de l’inscription · Réessaie dans un instant'}
              {(status === 'idle' || status === 'submitting') &&
                'Désabonnement en 1 clic · 4 800 abonnés'}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
